import React, { useEffect } from "react";
import KeyReceiptSection from "./KeyReceiptSection";

/**
 * Hand-rolled, NON-animated overlay for the key receipt (Schlüsselquittung).
 *
 * Deliberately NOT the shadcn Dialog (`components/ui/dialog.jsx`): that one
 * animates (zoom-in/slide/duration-200), which can make the SignaturePad canvas
 * mis-measure its width via getBoundingClientRect() during the open transition.
 * This mirrors InspectionChecklistModal's plain fixed-overlay — full size
 * immediately, no transition — the proven canvas-in-modal blueprint.
 *
 * KeyReceiptSection is mounted only while `open` (conditional render below); its
 * internal `signOpen` toggle is untouched, so the two SignaturePads still mount
 * lazily after the modal is fully laid out (never pre-mounted).
 */
export default function KeyReceiptModal({ open, tenancyId, onClose, onSigned }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="key-receipt-modal-title"
    >
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-[14px] border border-black/10 bg-white shadow-xl dark:border-white/[0.06] dark:bg-[#141824]">
        <div className="flex items-start justify-between gap-3 border-b border-black/10 px-5 py-4 dark:border-white/[0.06]">
          <div className="min-w-0">
            <h4
              id="key-receipt-modal-title"
              className="m-0 text-lg font-semibold text-[#0f172a] dark:text-[#eef2ff]"
            >
              Schlüsselquittung
            </h4>
            <p className="mt-1 text-[12px] text-[#64748b] dark:text-[#93a4bf]">
              Schlüssel und Übergabe für dieses Mietverhältnis dokumentieren.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg border border-black/10 px-3 py-1.5 text-[13px] text-[#0f172a] hover:bg-slate-100 dark:border-white/[0.12] dark:text-[#eef2ff] dark:hover:bg-white/[0.04]"
          >
            Schließen
          </button>
        </div>
        <div className="max-h-[calc(92vh-88px)] overflow-y-auto px-5 py-4">
          <KeyReceiptSection tenancyId={tenancyId} onSigned={onSigned} />
        </div>
      </div>
    </div>
  );
}
